import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Ile } from '../types/Ile';
import { Region } from '../types/Region';
import { Commune } from '../types/Commune';
import { Localite } from '../types/Localite';
import { ileService } from '../services/IleService';
import { regionService } from '../services/RegionService';
import { communeService } from '../services/CommuneService';
import { localiteService } from '../services/LocaliteService';

interface Props {
  value?: { idIle?: number; idRegion?: number; idCommune?: number; idLocalite?: number } | null;
  onChange: (val: { idIle?: number; idRegion?: number; idCommune?: number; idLocalite?: number } | null) => void;
}

const LocalitySelector: React.FC<Props> = ({ value = null, onChange }) => {
  const { api } = useAuth();
  const [iles, setIles] = useState<Ile[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [localites, setLocalites] = useState<Localite[]>([]);

  const [selectedIle, setSelectedIle] = useState<number | undefined>(value?.idIle);
  const [selectedRegion, setSelectedRegion] = useState<number | undefined>(value?.idRegion);
  const [selectedCommune, setSelectedCommune] = useState<number | undefined>(value?.idCommune);
  const [selectedLocalite, setSelectedLocalite] = useState<number | undefined>(value?.idLocalite);

  useEffect(() => {
    if (!api) return;

    (async () => {
      const [ilesRes, regionsRes, communesRes, localitesRes] = await Promise.all([
        ileService.getAll(api),
        regionService.getAllRegions(api),
        communeService.getAll(api),
        localiteService.getAll(api),
      ]);

      setIles(ilesRes);
      setRegions(regionsRes);
      setCommunes(communesRes);
      setLocalites(localitesRes);
    })();
  }, [api]);

  useEffect(() => {
    onChange({ idIle: selectedIle, idRegion: selectedRegion, idCommune: selectedCommune, idLocalite: selectedLocalite });
  }, [selectedIle, selectedRegion, selectedCommune, selectedLocalite]);

  const filteredRegions = useMemo(() => {
    if (!selectedIle) return regions;
    return regions.filter(r => r.idIle === selectedIle);
  }, [regions, selectedIle]);

  const filteredCommunes = useMemo(() => {
    if (!selectedRegion) return communes;
    return communes.filter(c => c.idRegion === selectedRegion);
  }, [communes, selectedRegion]);

  const filteredLocalites = useMemo(() => {
    if (!selectedCommune) return localites;
    return localites.filter(l => l.idCommune === selectedCommune);
  }, [localites, selectedCommune]);

  return (
    <div className="row g-2">
      <div className="col-sm-6 col-md-3">
        <select
          className="form-select form-select-sm"
          value={selectedIle ?? ''}
          onChange={e => { setSelectedIle(e.target.value ? Number(e.target.value) : undefined); setSelectedRegion(undefined); setSelectedCommune(undefined); setSelectedLocalite(undefined); }}
        >
          <option value="">Îles</option>
          {iles.map(i => <option key={i.idIle} value={i.idIle}>{i.nomIle}</option>)}
        </select>
      </div>

      <div className="col-sm-6 col-md-3">
        <select
          className="form-select form-select-sm"
          value={selectedRegion ?? ''}
          onChange={e => { setSelectedRegion(e.target.value ? Number(e.target.value) : undefined); setSelectedCommune(undefined); setSelectedLocalite(undefined); }}
        >
          <option value="">Régions</option>
          {filteredRegions.map(r => <option key={r.idRegion} value={r.idRegion}>{r.nomRegion}</option>)}
        </select>
      </div>

      <div className="col-sm-6 col-md-3">
        <select
          className="form-select form-select-sm"
          value={selectedCommune ?? ''}
          onChange={e => { setSelectedCommune(e.target.value ? Number(e.target.value) : undefined); setSelectedLocalite(undefined); }}
        >
          <option value="">Communes</option>
          {filteredCommunes.map(c => <option key={c.idCommune} value={c.idCommune}>{c.nomCommune}</option>)}
        </select>
      </div>

      <div className="col-sm-6 col-md-3">
        <select
          className="form-select form-select-sm"
          value={selectedLocalite ?? ''}
          onChange={e => { setSelectedLocalite(e.target.value ? Number(e.target.value) : undefined); }}
        >
          <option value="">Localités</option>
          {filteredLocalites.map(l => <option key={l.idLocalite} value={l.idLocalite}>{l.nomLocalite}</option>)}
        </select>
      </div>
    </div>
  );
};

export default LocalitySelector;
